import { Injectable, inject } from '@angular/core';
import { Observable } from 'rxjs';
import {
  NucleusAfterSaveBehavior,
  NucleusTheme,
  NucleusUpdateMyProfile,
  NucleusUser,
  NucleusUserSelfServiceContract,
  UserControllerService,
} from '@nucleus/user-ui';
import { ServiceHandler } from '@nucleus/ui-base';
import { UserService } from '../user/user.service';
import { UIConfigurationService } from '../ui-configuration.service';
import { ThemeService } from '@nucleus/ui-common';

/**
 * Tasksense → NucleusUserSelfServiceContract. Backs &lt;lib-my-profile&gt;.
 *
 * After-save preference: bridges UIConfigurationService.crudStayInPageAfterSave
 * (localStorage-backed) + the @nucleus/user-ui OpenAPI client's
 * updateMyAfterSaveBehavior() to persist the choice on the backend.
 */
@Injectable({ providedIn: 'root' })
export class TasksenseUserSelfServiceAdapter implements NucleusUserSelfServiceContract {
  private readonly userService = inject(UserService);
  private readonly uiConfig = inject(UIConfigurationService);
  private readonly themeService = inject(ThemeService);
  private readonly userController = inject(UserControllerService);

  getMyProfile(): Observable<NucleusUser> {
    return this.fromCallback<NucleusUser>((h) =>
      this.userService.getMyProfile(h as unknown as ServiceHandler<unknown>),
    );
  }

  updateMyProfile(payload: NucleusUpdateMyProfile): Observable<NucleusUser> {
    return this.fromCallback<NucleusUser>((h) =>
      this.userService.updateMyProfile(
        {
          userId: payload.userId,
          fullName: payload.fullName,
          email: payload.email,
        },
        h as unknown as ServiceHandler<unknown>,
      ),
    );
  }

  requestAccess(roles: string[]): Observable<void> {
    return this.fromCallback<void>((h) =>
      this.userService.requestAccess({ roles }, h as unknown as ServiceHandler<unknown>),
    );
  }

  findMyPendingAccessRequests(): Observable<string[]> {
    return this.fromCallback<string[]>((h) =>
      this.userService.findMyPendingAccessRequests(h as unknown as ServiceHandler<unknown>),
    );
  }

  getAfterSaveBehavior(): NucleusAfterSaveBehavior {
    return this.uiConfig.crudStayInPageAfterSave() ? 'STAY' : 'BACK_TO_LIST';
  }

  setAfterSaveBehavior(behavior: NucleusAfterSaveBehavior): void {
    this.uiConfig.setAfterSave(behavior);
  }

  updateAfterSaveBehavior(behavior: NucleusAfterSaveBehavior): Observable<unknown> {
    this.userService.invalidateMyProfileCache();
    return this.userController.updateMyAfterSaveBehavior(behavior);
  }

  getTheme(): NucleusTheme {
    return this.themeService.theme();
  }

  setTheme(theme: NucleusTheme): void {
    if (this.themeService.theme() !== theme) {
      this.themeService.toggle();
    }
  }

  private fromCallback<T>(
    call: (handler: ServiceHandler<T>) => void,
  ): Observable<T> {
    return new Observable<T>((subscriber) => {
      const handler: ServiceHandler<T> = {
        onStart: () => {},
        onSuccess: (data: T) => {
          subscriber.next(data);
          subscriber.complete();
        },
        onError: (err: string) => subscriber.error(err),
        onComplete: () => {},
      };
      call(handler);
    });
  }
}
